import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { servicesQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import type { AuditLogFilters, AuditLogResponse } from '../types';
import { AUDIT_LOGS_PAGE_SIZE } from '../constants/pagination';

interface UseAuditLogsDataOptions {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
  isAuthLoading: boolean;
  accountId?: number;
}

/**
 * Normalizes filter params to a URLSearchParams string (excluding audit_log_id and cursor).
 * This is used for comparing URL states to detect external changes.
 */
function normalizeFilterParams(filters: Partial<AuditLogFilters>): string {
  const normalizedParams = new URLSearchParams();
  if (filters.user_id !== undefined) normalizedParams.set('user_id', filters.user_id.toString());
  if (filters.ip_addr !== undefined && filters.ip_addr !== '') normalizedParams.set('ip_addr', filters.ip_addr);
  if (filters.object_type !== undefined && filters.object_type !== '')
    normalizedParams.set('object_type', filters.object_type);
  if (filters.object_id !== undefined && filters.object_id !== '') normalizedParams.set('object_id', filters.object_id);
  if (filters.attribute_name !== undefined && filters.attribute_name !== '')
    normalizedParams.set('attribute_name', filters.attribute_name);
  if (filters.action !== undefined && filters.action !== '') normalizedParams.set('action', filters.action);
  if (filters.start_time !== undefined && filters.start_time !== '')
    normalizedParams.set('start_time', filters.start_time);
  if (filters.end_time !== undefined && filters.end_time !== '') normalizedParams.set('end_time', filters.end_time);
  if (filters.sort_order !== undefined && filters.sort_order !== 'desc')
    normalizedParams.set('sort_order', filters.sort_order);
  return normalizedParams.toString();
}

export function useAuditLogsData({ context, contextId, isAuthLoading, accountId }: UseAuditLogsDataOptions) {
  const { track } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture initial URL params - stable across ALL renders
  const lastWrittenParamsRef = useRef<string>('');

  // Initialize filters from URL params - lazy initializer only runs once on mount
  const [filters, setFilters] = useState<AuditLogFilters>(() => {
    const params = new URLSearchParams(searchParams.toString());
    const filters: AuditLogFilters = {
      limit: AUDIT_LOGS_PAGE_SIZE,
      sort_order: 'desc',
    };

    const userId = params.get('user_id');
    if (userId != null && userId !== '') {
      const parsedUserId = parseInt(userId, 10);
      if (!Number.isNaN(parsedUserId)) {
        filters.user_id = parsedUserId;
      }
    }

    const ipAddr = params.get('ip_addr');
    if (ipAddr != null && ipAddr !== '') filters.ip_addr = ipAddr;

    const objectType = params.get('object_type');
    if (objectType != null && objectType !== '') filters.object_type = objectType;

    const objectId = params.get('object_id');
    if (objectId != null && objectId !== '') filters.object_id = objectId;

    const attributeName = params.get('attribute_name');
    if (attributeName != null && attributeName !== '') filters.attribute_name = attributeName;

    const action = params.get('action');
    if (action != null && action !== '') filters.action = action;

    const startTime = params.get('start_time');
    if (startTime != null && startTime !== '') filters.start_time = startTime;

    const endTime = params.get('end_time');
    if (endTime != null && endTime !== '') filters.end_time = endTime;

    const sortOrder = params.get('sort_order');
    if (sortOrder === 'asc' || sortOrder === 'desc') filters.sort_order = sortOrder;

    // Initialize lastWrittenParamsRef with normalized params (only our filter params)
    lastWrittenParamsRef.current = normalizeFilterParams(filters);

    return filters;
  });
  const [accumulatedLogs, setAccumulatedLogs] = useState<AuditLogResponse | undefined>(undefined);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [shouldLoadUsers, setShouldLoadUsers] = useState(false);

  // Get audit log ID from URL for deep linking
  const auditLogIdParam = searchParams.get('audit_log_id');
  const auditLogId = useMemo(() => {
    if (auditLogIdParam != null && auditLogIdParam !== '' && /^\d+$/.test(auditLogIdParam)) {
      const parsed = parseInt(auditLogIdParam, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  }, [auditLogIdParam]);

  // Sync URL params to filters when URL changes externally (e.g., "Filter by this object" button)
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams.toString());

    // Extract filter values from URL params
    const userId = currentParams.get('user_id');
    const ipAddr = currentParams.get('ip_addr');
    const objectType = currentParams.get('object_type');
    const objectId = currentParams.get('object_id');
    const attributeName = currentParams.get('attribute_name');
    const action = currentParams.get('action');
    const startTime = currentParams.get('start_time');
    const endTime = currentParams.get('end_time');
    const sortOrder = currentParams.get('sort_order');

    // Build a filters object from URL params for normalization
    const urlFilters: Partial<AuditLogFilters> = {};
    if (userId != null && userId !== '') {
      const parsedUserId = parseInt(userId, 10);
      if (!Number.isNaN(parsedUserId)) {
        urlFilters.user_id = parsedUserId;
      }
    } else {
      urlFilters.user_id = undefined;
    }
    if (ipAddr != null && ipAddr !== '') urlFilters.ip_addr = ipAddr;
    else urlFilters.ip_addr = undefined;
    if (objectType != null && objectType !== '') urlFilters.object_type = objectType;
    else urlFilters.object_type = undefined;
    if (objectId != null && objectId !== '') urlFilters.object_id = objectId;
    else urlFilters.object_id = undefined;
    if (attributeName != null && attributeName !== '') urlFilters.attribute_name = attributeName;
    else urlFilters.attribute_name = undefined;
    if (action != null && action !== '') urlFilters.action = action;
    else urlFilters.action = undefined;
    if (startTime != null && startTime !== '') urlFilters.start_time = startTime;
    else urlFilters.start_time = undefined;
    if (endTime != null && endTime !== '') urlFilters.end_time = endTime;
    else urlFilters.end_time = undefined;
    if (sortOrder === 'asc' || sortOrder === 'desc') urlFilters.sort_order = sortOrder;
    else urlFilters.sort_order = 'desc';

    // Normalize params to only include filter params (exclude audit_log_id for comparison)
    const normalizedParamsString = normalizeFilterParams(urlFilters);

    // Only update if the URL changed from something OTHER than our own writes
    // This prevents syncing our own changes back into state
    if (normalizedParamsString !== lastWrittenParamsRef.current) {
      // Update filters with URL params
      setFilters((prev) => ({ ...prev, ...urlFilters }));
    }
  }, [searchParams]);

  // Sync filters to URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    // Build URL params from current filter state
    if (filters.user_id !== undefined) params.set('user_id', filters.user_id.toString());
    if (filters.ip_addr !== undefined && filters.ip_addr !== '') params.set('ip_addr', filters.ip_addr);
    if (filters.object_type !== undefined && filters.object_type !== '') params.set('object_type', filters.object_type);
    if (filters.object_id !== undefined && filters.object_id !== '') params.set('object_id', filters.object_id);
    if (filters.attribute_name !== undefined && filters.attribute_name !== '')
      params.set('attribute_name', filters.attribute_name);
    if (filters.action !== undefined && filters.action !== '') params.set('action', filters.action);
    if (filters.start_time !== undefined && filters.start_time !== '') params.set('start_time', filters.start_time);
    if (filters.end_time !== undefined && filters.end_time !== '') params.set('end_time', filters.end_time);
    // Only include sort_order if it's not the default (desc)
    if (filters.sort_order !== undefined && filters.sort_order !== 'desc') params.set('sort_order', filters.sort_order);

    // Preserve audit_log_id if present
    if (auditLogId != null) {
      params.set('audit_log_id', auditLogId.toString());
    }

    // Update the ref BEFORE setting search params to prevent race condition
    // Normalize params for comparison (exclude audit_log_id)
    lastWrittenParamsRef.current = normalizeFilterParams(filters);
    setSearchParams(params, { replace: true });
  }, [
    filters.user_id,
    filters.ip_addr,
    filters.object_type,
    filters.object_id,
    filters.attribute_name,
    filters.action,
    filters.start_time,
    filters.end_time,
    filters.sort_order,
    auditLogId,
    setSearchParams,
  ]);

  // Only create queries if we have the required data
  const shouldCreateQuery = contextId != null && !isAuthLoading && auditLogId == null;
  const shouldFetchLogById = contextId != null && auditLogId != null && !isAuthLoading;

  // Fetch audit logs based on context
  const accountAuditLogsQuery = servicesQuery.getAccountAuditLogs.useQuery({
    queryKey: ['accountAuditLogs', contextId, filters],
    queryData: {
      params: { account_id: contextId ?? 0 },
      query: filters,
    },
    enabled: context === 'account' && shouldCreateQuery,
  });

  const workspaceAuditLogsQuery = servicesQuery.getWorkspaceAuditLogs.useQuery({
    queryKey: ['workspaceAuditLogs', contextId, filters],
    queryData: {
      params: { environment_id: contextId ?? 0 },
      query: filters,
    },
    enabled: context === 'workspace' && shouldCreateQuery,
  });

  // Deep link queries
  const accountAuditLogByIdQuery = servicesQuery.getAccountAuditLogById.useQuery({
    queryKey: ['accountAuditLogById', contextId, auditLogId],
    queryData: {
      params: { account_id: contextId ?? 0, id: auditLogId ?? 0 },
    },
    enabled: context === 'account' && shouldFetchLogById,
  });

  const workspaceAuditLogByIdQuery = servicesQuery.getWorkspaceAuditLogById.useQuery({
    queryKey: ['workspaceAuditLogById', contextId, auditLogId],
    queryData: {
      params: { environment_id: contextId ?? 0, id: auditLogId ?? 0 },
    },
    enabled: context === 'workspace' && shouldFetchLogById,
  });

  // Fetch all users for the account to populate User dropdown filter (lazy loaded)
  const allUsersQuery = servicesQuery.getAllUsers.useQuery({
    queryKey: ['allUsers', accountId],
    queryData: {
      params: { account_id: accountId ?? 0 },
    },
    enabled: shouldLoadUsers && accountId != null && !isAuthLoading,
  });

  const auditLogsQuery = context === 'account' ? accountAuditLogsQuery : workspaceAuditLogsQuery;
  const singleLogQuery = context === 'account' ? accountAuditLogByIdQuery : workspaceAuditLogByIdQuery;

  // Restore accumulated logs from sessionStorage on mount
  useEffect(() => {
    if (!hasRestoredFromStorage && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        const stored = sessionStorage.getItem(key);
        if (stored != null && stored !== '') {
          const parsedData = JSON.parse(stored);
          setAccumulatedLogs(parsedData);
        }
      } catch (error) {
        // Silently fail if restoration fails
      }
      setHasRestoredFromStorage(true);
      setIsInitialMount(false);
    }
  }, [contextId, hasRestoredFromStorage, context]);

  // Accumulate logs when "Load More" is clicked
  useEffect(() => {
    if (auditLogsQuery?.data != null) {
      const newData = auditLogsQuery.data.body;

      if (filters.cursor != null && filters.cursor !== '') {
        setAccumulatedLogs((prev) => {
          if (prev == null) return newData;

          // Deduplicate audit logs
          const logsMap = new Map<number, (typeof newData.data)[0]>();
          [...(prev.data ?? []), ...(newData.data ?? [])].forEach((log) => {
            logsMap.set(log.id, log);
          });

          // Deduplicate users
          const usersMap = new Map<number, { user_id: number; email: string; deleted?: boolean }>();
          [...(prev.users ?? []), ...(newData.users ?? [])].forEach((user) => {
            usersMap.set(user.user_id, user);
          });

          return {
            data: Array.from(logsMap.values()),
            users: Array.from(usersMap.values()),
            pagination: newData.pagination,
          };
        });
      } else {
        setAccumulatedLogs(newData);
      }
    }
  }, [auditLogsQuery.data, filters.cursor]);

  // Save accumulated logs to sessionStorage
  useEffect(() => {
    if (accumulatedLogs != null && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        sessionStorage.setItem(key, JSON.stringify(accumulatedLogs));
      } catch (error) {
        // Ignore storage errors
      }
    }
  }, [accumulatedLogs, contextId, context]);

  // Reset accumulated logs when filters change
  useEffect(() => {
    if (isInitialMount) {
      return;
    }

    // Only reset if we're not paginating
    if (filters.cursor == null || filters.cursor === '') {
      setAccumulatedLogs(undefined);
      setHasRestoredFromStorage(false);
      if (contextId != null) {
        try {
          const key = `audit-logs-${context}-${contextId}`;
          sessionStorage.removeItem(key);
        } catch (error) {
          // Ignore errors
        }
      }
    }
  }, [
    filters.user_id,
    filters.ip_addr,
    filters.object_type,
    filters.object_id,
    filters.attribute_name,
    filters.action,
    filters.start_time,
    filters.end_time,
    filters.sort_order,
    filters.cursor,
    isInitialMount,
    contextId,
    context,
  ]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<AuditLogFilters>) => {
      setFilters((prev) => {
        // If only the cursor is being set (Load More), don't reset it
        // Otherwise, reset cursor when filter values change
        if (Object.keys(newFilters).length === 1 && 'cursor' in newFilters) {
          return { ...prev, ...newFilters };
        }
        return { ...prev, ...newFilters, cursor: undefined };
      });
      track('auditLogs', 'filtersChanged', {
        filterCount: Object.keys(newFilters).length,
      });
    },
    [track],
  );

  const handleLoadUsers = useCallback(() => {
    setShouldLoadUsers(true);
  }, []);

  return {
    filters,
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    handleFiltersChange,
    handleLoadUsers,
    isLoadingUsers: allUsersQuery.isLoading,
  };
}
